import React, { useState, useEffect, useCallback } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Pagination, PaginationItem, Card, TextField, Box, Stack, IconButton,
    Skeleton, Typography, Chip, InputAdornment, Alert, Fade,
    LinearProgress, CircularProgress, useTheme, alpha, Select, MenuItem,
    FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Divider, Tooltip,
} from "@mui/material";
import {
    MdInbox, MdSearch, MdClear, MdError, MdSchedule, MdCheckCircle,
    MdCancel, MdArrowBack, MdArrowForward, MdPhone, MdEmail, MdPerson,
    MdRefresh, MdFilterList, MdVisibility, MdEdit,
} from "react-icons/md";
import { FaMale, FaFemale, FaVenusMars } from "react-icons/fa";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const BASE_URL = "https://api.medstown.com/admin/modern-medicine";

const C = {
    primary: "#014D4D", secondary: "#02A0A0", accent: "#FFB800",
    success: "#4caf50", error: "#f44336", warning: "#ff9800",
    info: "#2196f3", purple: "#7c3aed",
};

const statusCfg = {
    pending: { color: C.warning, label: "Pending", icon: <MdSchedule size={12} /> },
    contacted: { color: C.info, label: "Contacted", icon: <MdPhone size={12} /> },
    completed: { color: C.success, label: "Completed", icon: <MdCheckCircle size={12} /> },
    cancelled: { color: C.error, label: "Cancelled", icon: <MdCancel size={12} /> },
};
const getSCfg = (s) => statusCfg[s] || statusCfg.pending;

const Enquiries = () => {
    const theme = useTheme();

    const [enquiries, setEnquiries] = useState([]);
    const [stats, setStats] = useState({ total: 0, byStatus: [], byPackage: [] });
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [packageFilter, setPackageFilter] = useState("");
    const [bookingForFilter, setBookingForFilter] = useState("");
    const [sort, setSort] = useState("new");
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 500);
        return () => clearTimeout(t);
    }, [search]);

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const { data } = await axios.get(`${BASE_URL}/enquiries/stats`);
            if (data?.data) setStats(data.data);
        } catch (e) { console.error(e); }
        finally { setStatsLoading(false); }
    }, []);

    const fetchEnquiries = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const p = new URLSearchParams({ page, limit: 10, sort });
            if (debouncedSearch) p.append("search", debouncedSearch);
            if (statusFilter) p.append("status", statusFilter);
            if (packageFilter) p.append("packageType", packageFilter);
            if (bookingForFilter) p.append("bookingFor", bookingForFilter);
            const { data } = await axios.get(`${BASE_URL}/enquiries?${p}`);
            if (data?.data) {
                setEnquiries(data.data.list || data.data.docs || []);
                setTotalPages(data.data.totalPages || 1);
                setTotalCount(data.data.totalCount || data.data.totalDocs || 0);
            }
        } catch (e) {
            setError(e.response?.data?.message || "Failed to fetch enquiries.");
            setEnquiries([]);
        } finally { setLoading(false); }
    }, [page, debouncedSearch, statusFilter, packageFilter, bookingForFilter, sort]);

    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { fetchEnquiries(); }, [fetchEnquiries]);

    const handleViewDetail = async (id) => {
        setDetailLoading(true); setDetailOpen(true);
        try {
            const { data } = await axios.get(`${BASE_URL}/enquiries/${id}`);
            if (data?.data) setSelectedEnquiry(data.data);
        } catch (e) { console.error(e); }
        finally { setDetailLoading(false); }
    };

    const handleStatusChange = async (id, s) => {
        setUpdatingStatus(id);
        try {
            await axios.patch(`${BASE_URL}/enquiries/${id}/status`, { status: s });
            fetchEnquiries(); fetchStats();
        } catch (e) { console.error(e); }
        finally { setUpdatingStatus(null); }
    };

    const cnt = (type, val) => {
        const arr = type === "s" ? stats.byStatus : stats.byPackage;
        return arr.find((i) => i._id === val)?.count || 0;
    };

    const clearFilters = () => {
        setSearch(""); setStatusFilter(""); setPackageFilter("");
        setBookingForFilter(""); setSort("new"); setPage(1);
    };

    const hasFilters = statusFilter || packageFilter || bookingForFilter || search;

    // Compact stat pill
    const StatPill = ({ label, value, color }) => (
        <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.8,
            px: 1.5, py: 0.5, borderRadius: 2,
            border: `1px solid ${alpha(color, 0.2)}`,
            backgroundColor: alpha(color, 0.06),
        }}>
            {statsLoading ? <Skeleton width={20} height={18} /> : (
                <Typography variant="caption" fontWeight={800} sx={{ color, fontSize: "0.8rem" }}>
                    {value}
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: "0.7rem" }}>
                {label}
            </Typography>
        </Box>
    );

    const smallSelect = { height: 32, fontSize: "0.78rem", borderRadius: 2 };

    return (
        <Box sx={{ mt: 2 }}>
            {/* Header + Stats row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: C.primary }}>
                    Nura Health - Enquiries
                </Typography>
                <Stack direction="row" spacing={0.8} flexWrap="wrap">
                    <StatPill label="Total" value={stats.total} color={C.primary} />
                    <StatPill label="Pending" value={cnt("s", "pending")} color={C.warning} />
                    <StatPill label="Contacted" value={cnt("s", "contacted")} color={C.info} />
                    <StatPill label="Completed" value={cnt("s", "completed")} color={C.success} />
                    <StatPill label="Men" value={cnt("p", "men")} color={C.purple} />
                    <StatPill label="Women" value={cnt("p", "women")} color="#e91e63" />
                </Stack>
            </Stack>

            {/* Main Card */}
            <Card sx={{
                borderRadius: 2.5,
                boxShadow: `0 2px 12px ${alpha("#000", 0.06)}`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                overflow: "hidden", position: "relative",
            }}>
                {loading && (
                    <LinearProgress sx={{
                        position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, height: 2,
                        "& .MuiLinearProgress-bar": {
                            background: `linear-gradient(90deg, ${C.primary}, ${C.secondary})`,
                        },
                    }} />
                )}

                {/* Compact Filters */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, flexWrap: "wrap", gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            width: 200,
                            "& .MuiOutlinedInput-root": {
                                height: 32, fontSize: "0.8rem", borderRadius: 2,
                                "& fieldset": { borderColor: alpha(C.primary, 0.2) },
                            },
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><MdSearch size={15} color={C.primary} /></InputAdornment>,
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.3 }}><MdClear size={14} /></IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={{ fontSize: "0.78rem" }}>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} sx={smallSelect}>
                            <MenuItem value="" dense>All</MenuItem>
                            <MenuItem value="pending" dense>Pending</MenuItem>
                            <MenuItem value="contacted" dense>Contacted</MenuItem>
                            <MenuItem value="completed" dense>Completed</MenuItem>
                            <MenuItem value="cancelled" dense>Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={{ fontSize: "0.78rem" }}>Package</InputLabel>
                        <Select value={packageFilter} label="Package" onChange={(e) => { setPackageFilter(e.target.value); setPage(1); }} sx={smallSelect}>
                            <MenuItem value="" dense>All</MenuItem>
                            <MenuItem value="men" dense>Men</MenuItem>
                            <MenuItem value="women" dense>Women</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={{ fontSize: "0.78rem" }}>Booking</InputLabel>
                        <Select value={bookingForFilter} label="Booking" onChange={(e) => { setBookingForFilter(e.target.value); setPage(1); }} sx={smallSelect}>
                            <MenuItem value="" dense>All</MenuItem>
                            <MenuItem value="self" dense>Self</MenuItem>
                            <MenuItem value="others" dense>Others</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel sx={{ fontSize: "0.78rem" }}>Sort</InputLabel>
                        <Select value={sort} label="Sort" onChange={(e) => { setSort(e.target.value); setPage(1); }} sx={smallSelect}>
                            <MenuItem value="new" dense>Newest</MenuItem>
                            <MenuItem value="old" dense>Oldest</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ flex: 1 }} />
                    {hasFilters && (
                        <Tooltip title="Clear filters">
                            <IconButton size="small" onClick={clearFilters} sx={{ p: 0.5, color: C.error }}>
                                <MdFilterList size={16} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => { fetchEnquiries(); fetchStats(); }} sx={{ p: 0.5, color: C.primary }}>
                            <MdRefresh size={16} />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {error && (
                    <Fade in><Alert severity="error" sx={{ mx: 1.5, mb: 1, borderRadius: 1.5, py: 0 }} icon={<MdError size={16} />}
                        action={<IconButton size="small" onClick={() => setError(null)}><MdClear size={14} /></IconButton>}>{error}</Alert></Fade>
                )}

                {/* Compact Table */}
                <TableContainer>
                    <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.8, px: 1.2, fontSize: "0.78rem" } }}>
                        <TableHead>
                            <TableRow sx={{
                                "& .MuiTableCell-head": {
                                    backgroundColor: alpha(C.primary, 0.05), fontWeight: 700,
                                    fontSize: "0.68rem", color: C.primary, border: "none",
                                    py: 1, textTransform: "uppercase", letterSpacing: "0.4px",
                                },
                            }}>
                                <TableCell>ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell>Pincode</TableCell>
                                <TableCell>Package</TableCell>
                                <TableCell>For</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell align="center" sx={{ width: 40 }}>•••</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && !enquiries.length ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>{Array.from({ length: 11 }).map((_, j) => (
                                        <TableCell key={j}><Skeleton height={20} sx={{ borderRadius: 1 }} /></TableCell>
                                    ))}</TableRow>
                                ))
                            ) : enquiries.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                                        <MdInbox size={28} color={alpha(C.primary, 0.3)} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {hasFilters ? "No results — try different filters" : "No enquiries yet"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                enquiries.map((item) => {
                                    const sc = getSCfg(item.status);
                                    return (
                                        <TableRow key={item._id || item.enquiryId} hover sx={{
                                            "&:hover": { backgroundColor: alpha(C.primary, 0.02) },
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                                        }}>
                                            <TableCell>
                                                <Box component="span" sx={{
                                                    fontFamily: "monospace", fontSize: "0.72rem", fontWeight: 600,
                                                    color: C.primary, backgroundColor: alpha(C.primary, 0.07),
                                                    px: 0.7, py: 0.2, borderRadius: 0.8,
                                                }}>{item.enquiryId}</Box>
                                            </TableCell>
                                            <TableCell><Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 120, fontSize: "0.78rem" }}>{item.name}</Typography></TableCell>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{item.phone}</TableCell>
                                            <TableCell>
                                                <Tooltip title={item.email || ""}>
                                                    <Typography noWrap sx={{ maxWidth: 130, fontSize: "0.75rem" }}>{item.email || "—"}</Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={item.address ? [item.address.details?.house, item.address.details?.apartment, item.address.street, item.address.district, item.address.city, item.address.region].filter(Boolean).join(", ") : ""}>
                                                    <Typography noWrap sx={{ maxWidth: 130, fontSize: "0.75rem" }}>
                                                        {item.address ? [item.address.city, item.address.district].filter(Boolean).join(", ") : "—"}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 600 }}>
                                                    {item.address?.postalCode || "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={item.packageType} size="small"
                                                    icon={item.packageType === "men" ? <FaMale size={10} /> : <FaFemale size={10} />}
                                                    sx={{
                                                        height: 20, fontSize: "0.65rem", fontWeight: 600, textTransform: "capitalize",
                                                        backgroundColor: alpha(item.packageType === "men" ? C.purple : "#e91e63", 0.1),
                                                        color: item.packageType === "men" ? C.purple : "#e91e63",
                                                        "& .MuiChip-icon": { color: "inherit", ml: 0.3 },
                                                        "& .MuiChip-label": { px: 0.6 },
                                                    }} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ textTransform: "capitalize", color: "text.secondary" }}>{item.bookingFor}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {updatingStatus === item.enquiryId ? <CircularProgress size={16} /> : (
                                                    <Select value={item.status} size="small"
                                                        onChange={(e) => handleStatusChange(item.enquiryId, e.target.value)}
                                                        sx={{
                                                            fontSize: "0.7rem", fontWeight: 600, height: 24, minWidth: 95,
                                                            borderRadius: 1.5, color: sc.color,
                                                            backgroundColor: alpha(sc.color, 0.07),
                                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(sc.color, 0.25) },
                                                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: sc.color },
                                                            "& .MuiSelect-icon": { color: sc.color, fontSize: "1rem" },
                                                            "& .MuiSelect-select": { py: 0.2, pr: "20px !important" },
                                                        }}>
                                                        <MenuItem value="pending" dense sx={{ fontSize: "0.75rem" }}>Pending</MenuItem>
                                                        <MenuItem value="contacted" dense sx={{ fontSize: "0.75rem" }}>Contacted</MenuItem>
                                                        <MenuItem value="completed" dense sx={{ fontSize: "0.75rem" }}>Completed</MenuItem>
                                                        <MenuItem value="cancelled" dense sx={{ fontSize: "0.75rem" }}>Cancelled</MenuItem>
                                                    </Select>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                                                    {dayjs(item.createdAt).format("DD MMM, hh:mm A")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton size="small" onClick={() => handleViewDetail(item.enquiryId)}
                                                    sx={{
                                                        p: 0.4, color: C.primary,
                                                        "&:hover": { backgroundColor: alpha(C.primary, 0.1) },
                                                    }}>
                                                    <MdVisibility size={15} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Compact Pagination */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        {enquiries.length} of {totalCount}
                    </Typography>
                    {totalPages > 1 && (
                        <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)}
                            size="small" shape="rounded"
                            renderItem={(item) => (
                                <PaginationItem slots={{ previous: MdArrowBack, next: MdArrowForward }} {...item}
                                    sx={{
                                        fontSize: "0.75rem", minWidth: 28, height: 28, borderRadius: 1.5,
                                        "&.Mui-selected": { backgroundColor: C.primary, color: "#fff" },
                                    }} />
                            )} />
                    )}
                </Stack>
            </Card>

            {/* Compact Detail Dialog */}
            <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setSelectedEnquiry(null); }}
                maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
                <DialogTitle sx={{
                    background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
                    color: "#fff", fontWeight: 700, fontSize: "0.95rem", py: 1.5,
                }}>Enquiry Details</DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    {detailLoading ? (
                        <Stack spacing={1.5} sx={{ py: 2 }}>
                            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={24} sx={{ borderRadius: 1 }} />)}
                        </Stack>
                    ) : selectedEnquiry ? (
                        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                            {[
                                { label: "ID", value: selectedEnquiry.enquiryId, mono: true },
                                { label: "Name", value: selectedEnquiry.name },
                                { label: "DOB", value: selectedEnquiry.dob },
                                { label: "Phone", value: selectedEnquiry.phone, mono: true },
                                { label: "Email", value: selectedEnquiry.email || "—" },
                                { label: "Package", value: selectedEnquiry.packageType, cap: true },
                                { label: "Booking For", value: selectedEnquiry.bookingFor, cap: true },
                                { label: "Address", value: selectedEnquiry.address ? [selectedEnquiry.address.details?.house, selectedEnquiry.address.details?.apartment, selectedEnquiry.address.street, selectedEnquiry.address.district, selectedEnquiry.address.city, selectedEnquiry.address.region].filter(Boolean).join(", ") : "—" },
                                { label: "Pincode", value: selectedEnquiry.address?.postalCode || "—", mono: true },
                            ].map((f, i) => (
                                <Stack key={i} direction="row" alignItems="baseline" spacing={1}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 70, fontSize: "0.7rem", textTransform: "uppercase" }}>
                                        {f.label}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{
                                        fontFamily: f.mono ? "monospace" : "inherit",
                                        textTransform: f.cap ? "capitalize" : "none", fontSize: "0.82rem",
                                    }}>{f.value}</Typography>
                                </Stack>
                            ))}
                            <Divider sx={{ my: 0.5 }} />
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 70, fontSize: "0.7rem", textTransform: "uppercase" }}>Status</Typography>
                                {(() => {
                                    const sc = getSCfg(selectedEnquiry.status); return (
                                        <Chip label={sc.label} icon={sc.icon} size="small" sx={{
                                            height: 22, fontSize: "0.7rem", fontWeight: 600,
                                            backgroundColor: alpha(sc.color, 0.1), color: sc.color,
                                            "& .MuiChip-icon": { color: sc.color },
                                        }} />
                                    );
                                })()}
                            </Stack>
                            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: "0.65rem" }}>CREATED</Typography>
                                    <Typography variant="caption" display="block" fontWeight={500}>{dayjs(selectedEnquiry.createdAt).format("DD MMM YYYY, hh:mm A")}</Typography>
                                    <Typography variant="caption" color="text.disabled">{dayjs(selectedEnquiry.createdAt).fromNow()}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: "0.65rem" }}>UPDATED</Typography>
                                    <Typography variant="caption" display="block" fontWeight={500}>{dayjs(selectedEnquiry.updatedAt).format("DD MMM YYYY, hh:mm A")}</Typography>
                                    <Typography variant="caption" color="text.disabled">{dayjs(selectedEnquiry.updatedAt).fromNow()}</Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>Not found</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 1.5 }}>
                    <Button size="small" onClick={() => { setDetailOpen(false); setSelectedEnquiry(null); }}
                        variant="outlined" sx={{ borderRadius: 1.5, fontSize: "0.78rem", borderColor: alpha(C.primary, 0.3), color: C.primary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Enquiries;
